/**
 * Build and filter AI triage candidate queues from clinical events
 * and local detector outputs.
 */

import type { ClinicalEvent } from "../types/clinicalEvent";
import type { DetectedSpike } from "../types/detectionTypes";
import type { SeizureEvent } from "../types/detectionTypes";
import {
  AI_TRIAGE_CATEGORIES,
  applyDispositionToPayload,
  defaultModelProvenance,
  dispositionToEventStatus,
  emptyDisposition,
  parseAuditTrailFromPayload,
  parseDispositionFromPayload,
  parseModelFromPayload,
  resolveCandidateCategory,
  type AITriageAuditEntry,
  type AITriageCandidate,
  type AITriageCandidateCategory,
  type AITriageDisposition,
  type AITriageDispositionStatus,
  type AITriageFalsePositiveClass,
  type AITriageModelProvenance,
} from "./aiTriageTypes";
import {
  buildHeuristicExplanationFields,
  mergeExplanationIntoPayload,
  parseExplanationFieldsFromPayload,
} from "./aiTriageExplainability";

export function clinicalEventToTriageCandidate(
  event: ClinicalEvent & { rowVersion?: number | null },
): AITriageCandidate {
  const confidence = event.confidenceScore ?? null;
  const existing = parseExplanationFieldsFromPayload(event.payload || {});
  const explanation = buildHeuristicExplanationFields({
    candidateId: event.eventId,
    category: resolveCandidateCategory(event.eventTypeCode, event.payload),
    confidence,
    channels: event.channels || [],
    startTimeSeconds: event.startTimeSeconds,
    endTimeSeconds: event.endTimeSeconds,
    recordingId: event.recordingId,
    existing,
  });
  return {
    candidateId: event.eventId,
    eventId: event.eventId,
    rowVersion: event.rowVersion ?? null,
    category: resolveCandidateCategory(event.eventTypeCode, event.payload),
    label: event.eventLabel,
    startTimeSeconds: event.startTimeSeconds,
    endTimeSeconds: event.endTimeSeconds ?? null,
    channels: event.channels || [],
    confidence,
    model: parseModelFromPayload(event.payload, confidence),
    disposition: parseDispositionFromPayload(event.payload),
    auditTrail: parseAuditTrailFromPayload(event.payload),
    sourceKind:
      event.sourceKind === "algorithm" ||
      event.sourceKind === "manual" ||
      event.sourceKind === "import" ||
      event.sourceKind === "system"
        ? event.sourceKind
        : "algorithm",
    eventTypeCode: event.eventTypeCode,
    payload: mergeExplanationIntoPayload(event.payload || {}, explanation),
  };
}

export function spikeToTriageCandidate(spike: DetectedSpike): AITriageCandidate {
  const model = defaultModelProvenance({
    modelName: "local-spike-detector",
    modelVersion: "1.0.0-local",
    thresholdSetId: "local-spike-default",
    thresholds: { minConfidence: 0.5 },
    inputWindow: {
      startTimeSeconds: spike.time,
      endTimeSeconds: spike.time + spike.duration / 1000,
      durationSeconds: spike.duration / 1000,
    },
    preprocessingVersion: "local-eeg-preprocess-v1",
    inputFeatures: ["amplitude", "duration", "sharpness", "riseTime", "fallTime"],
    confidence: spike.confidence,
  });
  const explanation = buildHeuristicExplanationFields({
    candidateId: spike.id,
    category: "spike",
    confidence: spike.confidence,
    channels: spike.channelName ? [spike.channelName] : [],
    startTimeSeconds: spike.time,
    endTimeSeconds: spike.time + spike.duration / 1000,
    features: model.inputFeatures,
  });
  return {
    candidateId: spike.id,
    eventId: null,
    rowVersion: null,
    category: "spike",
    label: `Spike · ${spike.channelName || `Ch${spike.channelIndex}`}`,
    startTimeSeconds: spike.time,
    endTimeSeconds: spike.time + spike.duration / 1000,
    channels: spike.channelName ? [spike.channelName] : [],
    confidence: spike.confidence,
    model,
    disposition: emptyDisposition("pending"),
    auditTrail: [],
    sourceKind: "local_detector",
    eventTypeCode: "ai_spike",
    payload: mergeExplanationIntoPayload(
      {
        kind: "ai_detection",
        category: "spike",
        model,
        morphology: spike.morphology,
      },
      explanation,
    ),
  };
}

export function seizureToTriageCandidate(
  seizure: SeizureEvent,
  channelNames: string[] = [],
): AITriageCandidate {
  const channels = seizure.channels.map(
    (index) => channelNames[index] || `Ch${index}`,
  );
  const model = defaultModelProvenance({
    modelName: "local-seizure-detector",
    modelVersion: "1.0.0-local",
    thresholdSetId: "local-seizure-default",
    thresholds: { avgProbability: 0.5 },
    inputWindow: {
      startTimeSeconds: seizure.startTime,
      endTimeSeconds: seizure.endTime,
      durationSeconds: seizure.duration,
    },
    preprocessingVersion: "local-eeg-preprocess-v1",
    inputFeatures: ["avgProbability", "peakProbability", "channelSet", "duration"],
    confidence: seizure.avgProbability,
  });
  const explanation = buildHeuristicExplanationFields({
    candidateId: seizure.id,
    category: "seizure",
    confidence: seizure.avgProbability,
    channels,
    startTimeSeconds: seizure.startTime,
    endTimeSeconds: seizure.endTime,
    features: model.inputFeatures,
  });
  return {
    candidateId: seizure.id,
    eventId: null,
    rowVersion: null,
    category: "seizure",
    label: `Seizure · ${seizure.type}`,
    startTimeSeconds: seizure.startTime,
    endTimeSeconds: seizure.endTime,
    channels,
    confidence: seizure.avgProbability,
    model,
    disposition: emptyDisposition("pending"),
    auditTrail: [],
    sourceKind: "local_detector",
    eventTypeCode: "ai_seizure",
    payload: mergeExplanationIntoPayload(
      {
        kind: "ai_detection",
        category: "seizure",
        model,
        seizureType: seizure.type,
        peakProbability: seizure.peakProbability,
      },
      explanation,
    ),
  };
}

export function buildAITriageQueue(input: {
  clinicalEvents?: ClinicalEvent[];
  spikes?: DetectedSpike[];
  seizures?: SeizureEvent[];
  channelNames?: string[];
}): AITriageCandidate[] {
  const fromEvents = (input.clinicalEvents || [])
    .filter((event) => {
      const category = resolveCandidateCategory(
        event.eventTypeCode,
        event.payload,
      );
      return (
        event.kind === "ai_detection" ||
        event.kind === "artifact" ||
        AI_TRIAGE_CATEGORIES.includes(category)
      );
    })
    .map((event) => clinicalEventToTriageCandidate(event));

  const eventIds = new Set(
    fromEvents.map((c) => c.eventId).filter(Boolean) as string[],
  );
  const localSpikes = (input.spikes || [])
    .filter((spike) => !eventIds.has(spike.id))
    .map(spikeToTriageCandidate);
  const localSeizures = (input.seizures || [])
    .filter((seizure) => !eventIds.has(seizure.id))
    .map((seizure) =>
      seizureToTriageCandidate(seizure, input.channelNames || []),
    );

  return [...fromEvents, ...localSpikes, ...localSeizures].sort(
    (a, b) => a.startTimeSeconds - b.startTimeSeconds,
  );
}

export function filterAITriageQueue(
  candidates: AITriageCandidate[],
  filters: {
    category?: AITriageCandidateCategory | "all";
    disposition?: AITriageDispositionStatus | "all";
    minConfidence?: number;
    search?: string;
  },
): AITriageCandidate[] {
  const category = filters.category || "all";
  const disposition = filters.disposition || "all";
  const minConfidence = filters.minConfidence ?? 0;
  const search = (filters.search || "").trim().toLowerCase();

  return candidates.filter((candidate) => {
    if (category !== "all" && candidate.category !== category) return false;
    if (disposition !== "all" && candidate.disposition.status !== disposition) {
      return false;
    }
    if (
      candidate.confidence != null &&
      candidate.confidence < minConfidence
    ) {
      return false;
    }
    if (search) {
      const haystack = [
        candidate.label,
        candidate.candidateId,
        candidate.category,
        candidate.eventTypeCode,
        candidate.channels.join(" "),
        candidate.model.modelName,
      ]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(search)) return false;
    }
    return true;
  });
}

export function buildDispositionUpdate(input: {
  candidate: AITriageCandidate;
  status: AITriageDispositionStatus;
  falsePositiveClass?: AITriageFalsePositiveClass | null;
  reviewerId?: string | null;
  notes?: string | null;
  reviewedAt?: Date;
}): {
  disposition: AITriageDisposition;
  auditEntry: AITriageAuditEntry;
  payload: Record<string, unknown>;
  eventStatus: "active" | "suppressed";
  model: AITriageModelProvenance;
} {
  const reviewedAtIso = (input.reviewedAt || new Date()).toISOString();
  const disposition: AITriageDisposition = {
    status: input.status,
    falsePositiveClass:
      input.status === "false_positive"
        ? input.falsePositiveClass || "other"
        : null,
    reviewerId: input.reviewerId ?? null,
    reviewedAtIso,
    notes: input.notes ?? null,
    schemaVersion: input.candidate.disposition.schemaVersion,
  };
  const auditEntry: AITriageAuditEntry = {
    atIso: reviewedAtIso,
    action: input.status,
    reviewerId: disposition.reviewerId,
    falsePositiveClass: disposition.falsePositiveClass,
    notes: disposition.notes,
  };
  const model = input.candidate.model;
  const explanation = buildHeuristicExplanationFields({
    candidateId: input.candidate.candidateId,
    category: input.candidate.category,
    confidence: input.candidate.confidence,
    channels: input.candidate.channels,
    startTimeSeconds: input.candidate.startTimeSeconds,
    endTimeSeconds: input.candidate.endTimeSeconds,
    features: model.inputFeatures,
    existing: parseExplanationFieldsFromPayload(input.candidate.payload),
  });
  const payload = mergeExplanationIntoPayload(
    applyDispositionToPayload(
      input.candidate.payload,
      disposition,
      auditEntry,
      model,
    ),
    explanation,
  );
  return {
    disposition,
    auditEntry,
    payload,
    eventStatus: dispositionToEventStatus(input.status),
    model,
  };
}

export function queueSummary(candidates: AITriageCandidate[]): {
  total: number;
  pending: number;
  confirmed: number;
  rejected: number;
  falsePositive: number;
  deferred: number;
  escalated: number;
  byCategory: Record<AITriageCandidateCategory, number>;
} {
  const byCategory = Object.fromEntries(
    AI_TRIAGE_CATEGORIES.map((category) => [category, 0]),
  ) as Record<AITriageCandidateCategory, number>;
  let pending = 0;
  let confirmed = 0;
  let rejected = 0;
  let falsePositive = 0;
  let deferred = 0;
  let escalated = 0;
  for (const candidate of candidates) {
    byCategory[candidate.category] += 1;
    switch (candidate.disposition.status) {
      case "pending":
        pending += 1;
        break;
      case "confirmed":
        confirmed += 1;
        break;
      case "rejected":
        rejected += 1;
        break;
      case "false_positive":
        falsePositive += 1;
        break;
      case "deferred":
        deferred += 1;
        break;
      case "escalated":
        escalated += 1;
        break;
    }
  }
  return {
    total: candidates.length,
    pending,
    confirmed,
    rejected,
    falsePositive,
    deferred,
    escalated,
    byCategory,
  };
}
